import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../redux/hooks";
import axios from "axios";
import {
  FiSearch,
  FiFilter,
  FiEye,
  FiTruck,
  FiClock,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import { cn } from "../../lib/utils";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import SalesOrderDetails from "../../components/modals/SalesOrderDetails";
import DeliverySummaryModal from "../OrderTracking/DeliverySummaryModal";

const CustomerOrders = () => {
  const [orders, setOrders] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string | null>(
    null,
  );
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const { token } = useAppSelector((store) => store.auth);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sales/orders`,
        {
          headers: {
            Authorization: token || "",
          },
        },
      );
      setOrders(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase() || "") {
      case "approved":
      case "completed":
        return "text-green-600 bg-green-50 border-green-100";
      case "pending":
        return "text-blue-600 bg-blue-50 border-blue-100";
      case "cancel":
      case "cancelled":
        return "text-red-600 bg-red-50 border-red-100";
      case "in transit":
        return "text-purple-600 bg-purple-50 border-purple-100";
      default:
        return "text-gray-600 bg-gray-50 border-gray-100";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase() || "") {
      case "approved":
      case "completed":
        return <FiCheckCircle className="text-xs" />;
      case "pending":
        return <FiClock className="text-xs" />;
      case "cancel":
      case "cancelled":
        return <FiXCircle className="text-xs" />;
      case "in transit":
        return <FiTruck className="text-xs" />;
      default:
        return null;
    }
  };

  const filteredOrders = orders.filter((order: any) => {
    const matchesSearch =
      order.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || order.status?.toLowerCase() === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = (orderNumber: string) => {
    setSelectedOrderNumber(orderNumber);
    setShowDetailsModal(true);
  };

  const handleTrackOrder = (orderNumber: string) => {
    setSelectedOrderNumber(orderNumber);
    setShowTrackingModal(true);
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-4 md:p-8">
      <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Orders</h1>
          <p className="text-gray-500 text-sm">
            Monitor and track all orders from your assigned customers
          </p>
        </div>
      </header>

      {/* Filters & Search */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-8 flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by client name, email, or order #..."
            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 transition-all text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <FiFilter className="text-gray-400" />
          <select
            className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="in transit">In Transit</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Order Number
                </th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <LoadingSpinner />
                      <p className="text-sm text-gray-400 font-medium">
                        Loading orders...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : filteredOrders.length > 0 ? (
                filteredOrders.map((order: any) => (
                  <tr
                    key={order._id}
                    className="hover:bg-gray-50/50 transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-[10px] font-bold">
                          {order.name?.charAt(0) || ""}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">
                            {order.name}
                          </p>
                          <p className="text-[10px] text-gray-400 font-medium">
                            {order.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-mono font-bold text-gray-600">
                        {order.orderNumber}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className={cn(
                          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider",
                          getStatusColor(order.status),
                        )}
                      >
                        {getStatusIcon(order.status)}
                        {order.status}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {order.createdAt
                        ? new Date(order.createdAt).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => handleViewDetails(order.orderNumber)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-100 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 transition-all shadow-sm"
                      >
                        <FiEye />
                        Details
                      </button>
                      <button
                        onClick={() => handleTrackOrder(order.orderNumber)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 border border-green-100 rounded-lg text-xs font-bold text-green-600 hover:bg-green-600 hover:text-white transition-all shadow-sm"
                      >
                        <FiTruck />
                        Track
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-gray-400"
                  >
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showDetailsModal && selectedOrderNumber && (
        <SalesOrderDetails
          orderNumber={selectedOrderNumber}
          onClose={() => setShowDetailsModal(false)}
        />
      )}

      {showTrackingModal && selectedOrderNumber && (
        <DeliverySummaryModal
          trackingId={selectedOrderNumber}
          onClose={() => setShowTrackingModal(false)}
        />
      )}
    </div>
  );
};

export default CustomerOrders;
